import * as React from 'react';
import ReactResizeDetector from 'react-resize-detector';
import * as _ from 'underscore';
import { TOTAL_CARD_WIDTH } from '../../app/spaces/CardView/CardViewStyles';
import { createContentButton } from '../../app/spaces/SubspaceHelpers';
import { Messages } from '../../codegen/Localize';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { ChannelStyles } from '../styles/ChannelStyles';
import { HomeStyles } from '../styles/HomeStyles';
import { ConnectedFavoritesChannel } from './ConnectedFavoritesChannel';
import { ConnectedRecentsChannel } from './ConnectedRecentsChannel';
import { ConnectedRecommendationsChannel } from './ConnectedRecommendationsChannel';
import { ConnectedWelcomeChannel } from './ConnectedWelcomeChannel';
import { LearnTableauChannel } from './LearnTableauChannel';
import { NoFavoritesAndRecentsChannel } from './NoFavoritesAndRecentsChannel';
import { TextChannel } from './TextChannel';

export interface HomeStateProps {
  readonly userSiteRole: string;
  readonly favoritesCount: number;
  readonly recentsCount: number;
  readonly popularCount: number;
  readonly recommendationsCount: number;
  readonly isFetchingFavorites: boolean;
  readonly isFetchingRecents: boolean;
  readonly isFetchingRecommendations: boolean;
  readonly showWelcomeChannel: boolean;
  readonly recommendationsEnabled: boolean;
}

export interface HomeDispatchProps {
  readonly enterHome: () => void;
}

export interface HomeState {
  readonly maxCardsPerRow: number;
  editorContent: string;
}

export type HomeProps = HomeStateProps & HomeDispatchProps;

export class Home extends React.PureComponent<HomeProps, HomeState> {

  private channelRef: React.RefObject<HTMLDivElement>;

  constructor(props: HomeProps) {
    super(props);
    this.state = {
      maxCardsPerRow: 0,
      editorContent: ''
    };
    this.channelRef = React.createRef();
  }

  public componentDidMount() {
    this.props.enterHome();
    this.updateMaxCardsPerRow();
  }

  /**
   * Calculates how many cards fit into the width of the channel. Used to help limit channels to N amount of rows.
   */
  private updateMaxCardsPerRow() {
    const channelWidth = this.channelRef.current.offsetWidth + ChannelStyles.horizontalMarginBetweenCards;  // The channel has a negative margin and can fit an additional margin between cards
    const cardWidth = TOTAL_CARD_WIDTH + ChannelStyles.horizontalMarginBetweenCards + ChannelStyles.cardBorder;
    const maxCardsPerRow = Math.floor(channelWidth / cardWidth);
    this.setState({ maxCardsPerRow });
  }

  public render() {
    const noFavsAndRecents = () => {
      return !this.props.isFetchingFavorites &&
        !this.props.isFetchingRecents &&
        this.props.favoritesCount === 0 &&
        this.props.recentsCount === 0;
    };

    const noRecommendations = () => {
      return !this.props.isFetchingRecommendations &&
        this.props.recommendationsCount === 0;
    };

    const createButton =
      <div className={HomeStyles.createButton}>
        {createContentButton(null, null)}
      </div>;

    return (
      <section className={HomeStyles.root} ref={this.channelRef}>
        <header className={HomeStyles.pageHeader}>
          <h1>{Messages.home_title()}</h1>
          {!this.props.showWelcomeChannel && this.props.userSiteRole !== SiteRoles.Viewer && createButton}
        </header>
        {this.props.showWelcomeChannel && <ConnectedWelcomeChannel />}
        {this.props.favoritesCount > 0 && <ConnectedFavoritesChannel maxCardsPerRow={this.state.maxCardsPerRow}></ConnectedFavoritesChannel>}
        {this.props.recentsCount > 0 && <ConnectedRecentsChannel maxCardsPerRow={this.state.maxCardsPerRow}></ConnectedRecentsChannel>}
        {noFavsAndRecents() && <NoFavoritesAndRecentsChannel />}
        {this.props.recommendationsCount > 0 &&
          this.props.recommendationsEnabled &&
          <ConnectedRecommendationsChannel maxCardsPerRow={this.state.maxCardsPerRow}></ConnectedRecommendationsChannel>
        }
        {this.props.recommendationsEnabled &&
          noRecommendations() &&
          <TextChannel title={Messages.home_recommendationsTitle()}>
            {Messages.home_emptyRecommendations()}
          </TextChannel>
        }
        <LearnTableauChannel userSiteRole={this.props.userSiteRole}></LearnTableauChannel>
        <ReactResizeDetector handleWidth handleHeight skipOnMount={true} onResize={() => _.debounce(this.updateMaxCardsPerRow.bind(this), 50)} />
      </section>
    );
  }
}
